import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Container, Divider, Button } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const Section = ({ title, children }) => (
  <Box sx={{ mb: 4 }}>
    <Typography variant="h6" sx={{ fontWeight: 700, color: '#1E3A5F', mb: 1.5, fontSize: '1rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
      {title}
    </Typography>
    <Box sx={{ color: '#444', lineHeight: 1.8, fontSize: '0.95rem' }}>
      {children}
    </Box>
  </Box>
);

function PrivacyPolicy() {
  const navigate = useNavigate();

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f0f4f8', py: 6 }}>
      <Container maxWidth="md">

        {/* Back Button */}
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ mb: 3, color: '#1E3A5F', textTransform: 'none', fontWeight: 600 }}
        >
          Back
        </Button>

        {/* Header */}
        <Box sx={{ mb: 5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <img src="/torch_logo.png" alt="Torch" style={{ width: 48, height: 48, objectFit: 'contain' }} />
            <Typography variant="h4" sx={{ fontWeight: 800, color: '#1E3A5F' }}>
              Privacy Policy
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: '#888' }}>
            Torch Beach Volleyball &nbsp;·&nbsp; Effective Date: March 13, 2026
          </Typography>
        </Box>

        {/* Card */}
        <Box sx={{ backgroundColor: 'white', borderRadius: 3, p: { xs: 3, sm: 5 }, boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}>

          <Section title="Overview">
            <Typography>
              Torch Beach Volleyball ("Torch," "we," "us," or "our") operates the Torch Beach VB application
              at <strong>app.torchvolleyball.com</strong>. This Privacy Policy explains what information we collect,
              how we use it, and how we protect it. We are committed to safeguarding the privacy of all our members,
              including junior athletes and their families.
            </Typography>
          </Section>

          <Divider sx={{ my: 3 }} />

          <Section title="Information We Collect">
            <Typography paragraph>We collect the following categories of information when you register and use the app:</Typography>
            <Box component="ul" sx={{ pl: 2, mt: 0 }}>
              <li><strong>Account Information:</strong> Name, email address, phone number, and password (managed via Google Firebase Authentication).</li>
              <li style={{ marginTop: 8 }}><strong>Athlete Information:</strong> Athlete name, skill group, school, grade level (for junior athletes), shirt size, and session balance.</li>
              <li style={{ marginTop: 8 }}><strong>Parent/Guardian Information:</strong> Name and contact details linked to junior athlete accounts.</li>
              <li style={{ marginTop: 8 }}><strong>Payment Information:</strong> Session plan purchases are processed through Stripe. We do not store credit card numbers or banking details — all payment data is handled securely by Stripe.</li>
              <li style={{ marginTop: 8 }}><strong>Attendance & Check-In Records:</strong> Class attendance, session usage, and check-in timestamps.</li>
              <li style={{ marginTop: 8 }}><strong>Coach Information:</strong> Name, contact details, pay rates, and class history for coaches and assistant coaches.</li>
            </Box>
          </Section>

          <Divider sx={{ my: 3 }} />

          <Section title="How We Use Your Information">
            <Box component="ul" sx={{ pl: 2, mt: 0 }}>
              <li>To manage athlete registrations, session balances, and class scheduling.</li>
              <li style={{ marginTop: 8 }}>To process payments and maintain purchase history through Stripe.</li>
              <li style={{ marginTop: 8 }}>To send low-balance notifications and important program communications via email or SMS.</li>
              <li style={{ marginTop: 8 }}>To track coach hours and compensation.</li>
              <li style={{ marginTop: 8 }}>To generate administrative reports for club operations.</li>
              <li style={{ marginTop: 8 }}>To allow parents to monitor their athlete's session balance and attendance.</li>
            </Box>
          </Section>

          <Divider sx={{ my: 3 }} />

          <Section title="Children's Privacy">
            <Typography>
              Torch Beach Volleyball collects information about junior athletes (under 18) solely for the purpose of
              managing their participation in the program. This information is linked to a parent or guardian account
              and is never shared with third parties for marketing or advertising purposes. Parents may contact us at
              any time to review, update, or request deletion of their child's information.
            </Typography>
          </Section>

          <Divider sx={{ my: 3 }} />

          <Section title="Data Sharing">
            <Typography paragraph>We do not sell your personal information. We share data only in the following limited circumstances:</Typography>
            <Box component="ul" sx={{ pl: 2, mt: 0 }}>
              <li><strong>Stripe:</strong> Payment processing. Stripe's privacy policy applies to payment data.</li>
              <li style={{ marginTop: 8 }}><strong>Google Firebase:</strong> Authentication and database hosting. Google's privacy policy applies.</li>
              <li style={{ marginTop: 8 }}><strong>Twilio:</strong> SMS notifications. Only phone numbers are shared for the purpose of sending messages.</li>
              <li style={{ marginTop: 8 }}><strong>Legal Requirements:</strong> We may disclose information if required by law or to protect the safety of our members.</li>
            </Box>
            <Typography sx={{ mt: 2 }}>
              Mobile phone numbers and SMS opt-in consent are not shared with, or sold to, third parties or
              affiliates for marketing or promotional purposes. SMS consent is used solely to deliver the
              notifications described in our SMS Terms &amp; Conditions.
            </Typography>
          </Section>

          <Divider sx={{ my: 3 }} />

          <Section title="Data Security">
            <Typography>
              All data is stored in Google Firebase, which uses industry-standard encryption in transit and at rest.
              Access to member data is role-restricted — coaches, parents, and athletes only see information
              relevant to their role. Administrative data is accessible only to authorized Torch staff.
            </Typography>
          </Section>

          <Divider sx={{ my: 3 }} />

          <Section title="Data Retention">
            <Typography>
              We retain member data for as long as your account is active or as needed to provide services.
              If you wish to have your account and associated data deleted, please contact us and we will
              process your request within 30 days.
            </Typography>
          </Section>

          <Divider sx={{ my: 3 }} />

          <Section title="Your Rights">
            <Typography paragraph>You have the right to:</Typography>
            <Box component="ul" sx={{ pl: 2, mt: 0 }}>
              <li>Access the personal information we hold about you.</li>
              <li style={{ marginTop: 8 }}>Request corrections to inaccurate information.</li>
              <li style={{ marginTop: 8 }}>Request deletion of your account and personal data.</li>
              <li style={{ marginTop: 8 }}>Opt out of non-essential communications.</li>
            </Box>
          </Section>

          <Divider sx={{ my: 3 }} />

          <Section title="Contact Us">
            <Typography>
              If you have any questions about this Privacy Policy or how your data is handled, please contact us at:
            </Typography>
            <Box sx={{ mt: 2, p: 2.5, backgroundColor: '#f0f4f8', borderRadius: 2, borderLeft: '4px solid #C9A84C' }}>
              <Typography sx={{ fontWeight: 700, color: '#1E3A5F' }}>Torch Beach Volleyball</Typography>
              <Typography sx={{ color: '#555', mt: 0.5 }}>Email: <a href="mailto:info@torchvolleyball.com" style={{ color: '#1E3A5F' }}>info@torchvolleyball.com</a></Typography>
              <Typography sx={{ color: '#555' }}>Website: <a href="https://app.torchvolleyball.com" style={{ color: '#1E3A5F' }}>torchvolleyball.com</a></Typography>
            </Box>
          </Section>

          <Divider sx={{ my: 3 }} />

          <Typography variant="body2" sx={{ color: '#aaa', textAlign: 'center', mt: 2 }}>
            This policy may be updated from time to time. Continued use of the app constitutes acceptance of any changes.
          </Typography>

        </Box>
      </Container>
    </Box>
  );
}

export default PrivacyPolicy;
